import React, {FC} from "react";
import {Button, Checkbox, Form, Input, Radio, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {formatMoney} from "../../../format";
import {orderPublicRegistrationOptionsByDependency} from "../../publicRegistration/publicRegistrationModel";
import {normalizeEventActivityQuestionType, resolveVisibleActivitySelection, toPositiveIds} from "./participantDetailHelpers";
import type {EventActivityOption, EventActivityOptionChoice} from "./participantDetailTypes";
import {DetailPanel} from "./participantDetailUi";
import {ParticipantFormField} from "./ParticipantFormField";
import type {ParticipantDetailModel} from "./useParticipantDetail";

const formatActivityOptionLabel = (option: EventActivityOption) => {
    const label = (option.labelHr || option.labelEn || `#${option.id}`).trim();
    const price = Number(option.price ?? 0);
    return price > 0 ? `${label} (${formatMoney(price)})` : label;
};

const formatActivityChoiceLabel = (choice: EventActivityOptionChoice) =>
    (choice.labelHr || choice.labelEn || choice.value || `#${choice.id}`).trim();

const activeChoiceOptions = (option: EventActivityOption) => (option.choices ?? [])
    .filter(choice => choice?.isActive !== false)
    .slice()
    .sort((left, right) => Number(left.order ?? 0) - Number(right.order ?? 0) || left.id - right.id)
    .map(choice => ({
        value: choice.id,
        label: formatActivityChoiceLabel(choice)
    }));

// Activity answers on the primary registration, shown with the public form's rules; locked once a
// participation offer exists.
export const ActivitiesSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const {primaryRegistration, activities, pending, actions, forms} = model;
    const disabled = !primaryRegistration || activities.isLocked;
    const watchedYesNoOptionIds = Form.useWatch("yesNoOptionIds", forms.activity) as number[] | undefined;
    // Same visibility as the public form: fee-type restrictions and dependent activities.
    const {visibleOptions} = resolveVisibleActivitySelection(
        activities.options,
        primaryRegistration?.selectedParticipationFeeTypeId,
        toPositiveIds(watchedYesNoOptionIds));
    const yesNoOptions = orderPublicRegistrationOptionsByDependency(visibleOptions)
        .filter(option => normalizeEventActivityQuestionType(option.questionType) === "YesNo");
    const answerOptions = visibleOptions
        .filter(option => normalizeEventActivityQuestionType(option.questionType) !== "YesNo");

    const renderAnswerControl = (option: EventActivityOption) => {
        const questionType = normalizeEventActivityQuestionType(option.questionType);
        const label = formatActivityOptionLabel(option);
        const name = ["answers", String(option.id)];

        if (questionType === "SingleChoice") {
            return (
                <ParticipantFormField key={option.id} label={label} name={name}>
                    <Radio.Group disabled={disabled} options={activeChoiceOptions(option)}/>
                </ParticipantFormField>
            );
        }

        if (questionType === "MultiChoice") {
            return (
                <ParticipantFormField key={option.id} label={label} name={name}>
                    <Checkbox.Group disabled={disabled} options={activeChoiceOptions(option)}/>
                </ParticipantFormField>
            );
        }

        return (
            <ParticipantFormField key={option.id} label={label} name={name}>
                <Input.TextArea aria-label={label} disabled={disabled} rows={3}/>
            </ParticipantFormField>
        );
    };

    return (
        <DetailPanel
            title={t("activities", {defaultValue: "Activities"})}
            extra={(
                <Button
                    type="primary"
                    loading={pending.savingActivities}
                    disabled={disabled || activities.options.length === 0}
                    onClick={() => forms.activity.submit()}
                >
                    {t("save-activities", {defaultValue: "Save activities"})}
                </Button>
            )}
            description={!primaryRegistration
                ? t("participant-register-before-activities", {
                    defaultValue: "Register the participant first so activities can be saved to the registration."
                })
                : activities.isLocked
                    ? t("participant-activities-locked-after-offer", {
                        defaultValue: "Activities are edited before the participation offer is created."
                    })
                    : undefined}
        >
            {activities.options.length > 0 ? (
                <Form form={forms.activity} onFinish={actions.saveActivities}>
                    <div className="ati-stack">
                        {yesNoOptions.length > 0 ? (
                            <Form.Item name="yesNoOptionIds" noStyle>
                                <Checkbox.Group disabled={disabled} style={{display: "flex", flexDirection: "column", gap: 8}}>
                                    {yesNoOptions.map(option => (
                                        <Checkbox
                                            key={option.id}
                                            value={option.id}
                                            style={option.visibilityDependsOnEventOptionId ? {marginInlineStart: 24} : undefined}
                                        >
                                            {formatActivityOptionLabel(option)}
                                        </Checkbox>
                                    ))}
                                </Checkbox.Group>
                            </Form.Item>
                        ) : null}

                        {answerOptions.map(renderAnswerControl)}
                    </div>
                </Form>
            ) : (
                <Typography.Text type="secondary">
                    {t("participant-activities-empty", {defaultValue: "No activities available to show."})}
                </Typography.Text>
            )}
        </DetailPanel>
    );
};
